import React from 'react';
import type { User, BusinessUser } from '../../../../types/usersDemo';
import { HeadingSmall } from 'baseui/typography';
import { getStatusColor } from '../../utils/helpers';
import { useSupportTickets } from '../../../../hooks/useDrawerData';

interface SupportTabProps {
  contact: User | BusinessUser;
  editingId: number | null;
  setEditingId: (id: number | null) => void;
  setPendingChange: (c: { id: number; newStatus: string; type: 'support' }) => void;
}

export const SupportTab: React.FC<SupportTabProps> = ({
  contact,
  editingId,
  setEditingId,
  setPendingChange,
}) => {
  const { tickets, loading, error } = useSupportTickets(contact);
  return (
    <div>
      <HeadingSmall style={{ margin: '0 0 16px 0', color: '#666' }}>Support Tickets</HeadingSmall>
      <div style={{ overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
              <th style={{ padding: '8px', textAlign: 'left' }}>#</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Ticket</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Subject</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Description</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Priority</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Creation Date</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket, index) => (
              <tr key={ticket.id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                <td style={{ padding: '8px' }}>{index + 1}</td>
                <td style={{ padding: '8px', fontWeight: '500' }}>{ticket.ticket}</td>
                <td style={{ padding: '8px' }}>{(ticket as any).title || ticket.ticket}</td>
                <td style={{ padding: '8px', color: '#666' }}>{ticket.description}</td>
                <td style={{ padding: '8px' }}>
                  <div
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: ticket.priority === 'High' ? '#FDE8E8' : 
                                     ticket.priority === 'Medium' ? '#FFF4E6' : '#E8F5E8',
                      color: ticket.priority === 'High' ? '#C53030' : 
                             ticket.priority === 'Medium' ? '#B7791F' : '#2D7738',
                      fontSize: '12px',
                      fontWeight: '500',
                      textAlign: 'center',
                      minWidth: '60px'
                    }}
                  >
                    {ticket.priority}
                  </div>
                </td>
                <td style={{ padding: '8px' }}>{ticket.creationDate}</td>
                <td style={{ padding: '8px' }}>
                  <div style={{ position: 'relative' }}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`Change status, current ${ticket.status}`}
                      aria-haspopup="listbox"
                      aria-expanded={editingId === ticket.id}
                      aria-controls={editingId === ticket.id ? `ticket-status-menu-${ticket.id}` : undefined}
                      onClick={() => setEditingId(editingId === ticket.id ? null : ticket.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingId(editingId === ticket.id ? null : ticket.id); } }}
                      style={{
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: getStatusColor(ticket.status) === 'positive' ? '#E8F5E8' : 
                                       getStatusColor(ticket.status) === 'warning' ? '#FFF4E6' : 
                                       getStatusColor(ticket.status) === 'negative' ? '#FDE8E8' : '#F0F0F0',
                        color: getStatusColor(ticket.status) === 'positive' ? '#2D7738' : 
                               getStatusColor(ticket.status) === 'warning' ? '#B7791F' : 
                               getStatusColor(ticket.status) === 'negative' ? '#C53030' : '#666',
                        fontSize: '12px',
                        fontWeight: '500',
                        textAlign: 'center',
                        minWidth: '60px'
                      }}
                    >
                      {ticket.status}
                    </div>

                    {editingId === ticket.id && (
                      <div 
                        role="listbox"
                        id={`ticket-status-menu-${ticket.id}`}
                        data-status-dropdown
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: '0',
                          right: '0',
                          backgroundColor: 'white',
                          border: '1px solid #e9ecef',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 1000,
                          marginTop: '2px'
                        }}
                      >
                        {['Open', 'In Progress', 'Closed'].filter(s => s !== ticket.status).map((status) => (
                          <div
                            role="option"
                            aria-selected={false}
                            key={status}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              borderBottom: '1px solid #f0f0f0',
                              backgroundColor: 'transparent',
                              color: '#333',
                              userSelect: 'none'
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPendingChange({ id: ticket.id, newStatus: status, type: 'support' });
                              setEditingId(null);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {status}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SupportTab;


